import {
  createContext,
  MutableRefObject,
  useContext,
  useMemo,
  useRef,
} from 'react'
import { useInterpret } from '@xstate/react'
import { usersMachine } from '../../machines/usersMachine'
import { create, read, update, list, dcAccess } from 'api'
import { CreateProps } from 'api/create'
import { UpdateProps } from 'api/update'
import { AnyUserData, StudentData } from 'types'
import { ReadProps, ReadResponseProps } from 'api/read'
import { ListProps } from 'api/list'
import { XStateService } from 'machines'
import { deactivate, DeactivateProps } from 'api/deactivate-user'
import { sendInstructions, SendInstructionsProps } from 'api/send-instructions'
import { clearDevices, ClearDevicesProps } from 'api/clear-devices'
import { indexRecord, IndexRecordProps } from 'api/index-record'
import { reinstate, ReinstateProps } from 'api/reinstate-user'
import { reactivate, ReactivateProps } from 'api/reactivate-user'
import { progress, UpdateProgressProps } from 'api/updateProgress-user'
import { contentMachine } from 'helpCenter/machines/contentMachine'
import {
  CreateLocationProps,
  TeachingLocation,
  UpdateLocationProps,
} from 'types/location'
import {
  createLocation,
  updateLocation,
  ReadLocationProps,
  readLocation,
} from 'api/locations'
import { STUDENT_DC, STUDENT_INPERSON, TEACHER } from '../../constants'
import { OrgData } from 'types/orgs'
import { listOrgs } from 'api/orgs'
import { estimateReinstatedDeactivationDate } from 'utils/digitalCourseStudents'
import {
  resendLMSSignatureRequest,
  ResendLMSSignatureRequestProps,
  ResendLMSSignatureRequestResponseProps,
} from 'api/resend-lms-signature-request'
import {
  reactivateTMATT,
  ReactivateTMATTProps,
  ReactivateTMATTResponseProps,
} from 'api/reactivate-tmatt'
import {
  deactivateTMATT,
  DeactivateTMATTProps,
  DeactivateTMATTResponseProps,
} from 'api/deactivate-tmatt'
import {
  enrollTMATT,
  EnrollTMATTProps,
  EnrollTMATTResponseProps,
} from 'api/enroll-tmatt'

export type UserActions = {
  create: (input: CreateProps) => Promise<{ success: boolean; id?: string }>
  update: (input: UpdateProps) => Promise<{ success: boolean }>
  read: (input: ReadProps) => Promise<ReadResponseProps>
  list: (
    input: ListProps
  ) => Promise<{ results?: Partial<AnyUserData>[]; total?: number }>
  account: {
    deactivate: (
      input: DeactivateProps
    ) => Promise<{ success?: boolean; error?: string | any }>
    lock: (input: {
      userId: string
    }) => Promise<{ success?: boolean; error?: string | any }>
    unlock: (input: {
      userId: string
    }) => Promise<{ success?: boolean; error?: string | any }>
    sendInstructions: (
      input: SendInstructionsProps
    ) => Promise<{ success?: boolean; error?: string | any }>
    clearDevices: (
      input: ClearDevicesProps
    ) => Promise<{ success?: boolean; error?: string | any }>
    indexRecord: (
      input: IndexRecordProps
    ) => Promise<{ success?: boolean; error?: string | any }>
    reinstate: (
      input: ReinstateProps
    ) => Promise<{ success?: boolean; error?: string | any }>
    reactivate: (
      input: ReactivateProps
    ) => Promise<{ success?: boolean; error?: string | any }>
    updateProgress: (
      input: UpdateProgressProps
    ) => Promise<{ success?: boolean; error?: string | any }>
    resendLMSSignatureRequest: (
      input: ResendLMSSignatureRequestProps
    ) => Promise<ResendLMSSignatureRequestResponseProps>
    reactivateTMATT: (
      input: ReactivateTMATTProps
    ) => Promise<ReactivateTMATTResponseProps>
    deactivateTMATT: (
      input: DeactivateTMATTProps
    ) => Promise<DeactivateTMATTResponseProps>
    enrollTMATT: (input: EnrollTMATTProps) => Promise<EnrollTMATTResponseProps>
  }
}

export type LocationActions = {
  read: (input: ReadLocationProps) => Promise<TeachingLocation>
  create: (input: CreateLocationProps) => Promise<TeachingLocation>
  update: (input: UpdateLocationProps) => Promise<TeachingLocation>
}

export type OrgActions = {
  list: (input: { countryCode: string }) => Promise<OrgData[]>
}

export type Requested = {
  orgs: {
    list: { input: { countryCode: string } }[]
  }
}

const usersContext = createContext(
  {} as {
    users: {
      service: XStateService
      actions: UserActions
    }
    locations: {
      service: XStateService
      actions: LocationActions
    }
    orgs: {
      service: XStateService
      actions: OrgActions
    }
    requested: MutableRefObject<Requested>
  }
)

export const UsersProvider = (props: { children: JSX.Element }) => {
  const usersService = useInterpret(usersMachine)
  const locationsService = useInterpret(contentMachine)
  const orgService = useInterpret(contentMachine)
  const requested = useRef<Requested>({
    orgs: {
      list: [] as { input: { countryCode: string } }[],
    },
  })

  const { userActions, locationActions, orgActions } = useMemo(
    () => getActions({ usersService, locationsService, orgService, requested }),
    [usersService, locationsService, orgService]
  )

  return (
    <usersContext.Provider
      value={{
        users: {
          service: usersService,
          actions: userActions,
        },
        locations: {
          service: locationsService,
          actions: locationActions,
        },
        orgs: {
          service: orgService,
          actions: orgActions,
        },
        requested,
      }}
    >
      {props.children}
    </usersContext.Provider>
  )
}

export const useUsersService = () => {
  return useContext(usersContext).users.service
}
export const useUsersActions = () => {
  return useContext(usersContext).users.actions
}

export const useLocationsService = () => {
  return useContext(usersContext).locations.service
}
export const useLocationsActions = () => {
  return useContext(usersContext).locations.actions
}

export const useOrgsService = () => {
  return useContext(usersContext).orgs.service
}
export const useOrgsActions = () => {
  return useContext(usersContext).orgs.actions
}

export const useRequested = () => {
  return useContext(usersContext).requested
}
const getActions = ({
  orgService,
  usersService,
  locationsService,
  requested,
}: {
  orgService: XStateService
  usersService: XStateService
  locationsService: XStateService
  requested: MutableRefObject<{
    orgs: { list: { input: { countryCode: string } }[] }
  }>
}) => {
  const readWithEffect = async (input: ReadProps) => {
    const resp = await read(input)
    if (resp.success) {
      const user = { ...resp.user }
      usersService.send('READ', {
        data: { user },
      })

      if (user.locationData) {
        locationsService.send('LOAD', {
          data: user.locationData,
        })
      }

      return resp
    } else {
      throw Error(resp.error)
    }
  }
  return {
    userActions: {
      create: async (input: CreateProps) => {
        const resp = await create(input)
        if (resp.success) {
          const user = {
            ...input.formData,
            id: resp.id,
          } as Partial<StudentData>
          if (input.formData?.userType === TEACHER) {
            user.status = 'waiting'
          } else if (
            input.formData?.userType === STUDENT_DC ||
            input.formData?.userType === STUDENT_INPERSON
          ) {
            user.status = 'locked'
          }
          usersService.send('CREATE', {
            data: { user },
          })
          return resp
        } else {
          if (resp.error === 'Teacher ID is invalid for student creation') {
            throw Error(`Authorization issues, try refreshing the page`)
          } else {
            const error = Error(resp.error) as Error & {
              code?: string
              existing?: Record<string, unknown>
            }
            error.code = resp.code
            error.existing = resp.data?.existing
            throw error
          }
        }
      },
      update: async (input: UpdateProps) => {
        const resp = await update(input)
        if (resp.success) {
          const user = { ...input.formData } as AnyUserData

          if (
            input.formData?.userType === STUDENT_DC ||
            input.formData?.userType === STUDENT_INPERSON
          ) {
            user.meditatorUserType = input.formData?.userType
          }
          if (input.formData?.teacher) {
            user.teacherID = input.formData?.teacher
          }
          usersService.send('UPDATE', {
            data: { user },
          })
          return resp
        } else {
          throw Error(resp.error)
        }
      },
      read: readWithEffect,
      list: async (input: ListProps) => {
        const resp = await list(input)
        if (resp.success) {
          const users = resp.results
          usersService.send('LOAD_USERS', {
            data: { users },
          })
          return resp
        } else {
          throw Error(resp.error)
        }
      },
      account: {
        deactivate: async (input: DeactivateProps) => {
          const resp = await deactivate(input)
          if (resp.success) {
            usersService.send('UPDATE', {
              data: { user: { id: input.userId, status: 'disabled' } },
            })
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        lock: async (input: { userId: string }) => {
          const resp = await dcAccess({ ...input, unlock: false })
          if (resp.success) {
            usersService.send('UPDATE', {
              data: { user: { id: input.userId, status: 'locked' } },
            })
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        unlock: async (input: { userId: string }) => {
          const resp = await dcAccess({ ...input, unlock: true })
          if (resp.success) {
            usersService.send('UPDATE', {
              data: { user: { id: input.userId, status: 'unlocked' } },
            })
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        sendInstructions: async (input: SendInstructionsProps) => {
          const resp = await sendInstructions(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        clearDevices: async (input: ClearDevicesProps) => {
          const resp = await clearDevices(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        indexRecord: async (input: IndexRecordProps) => {
          const resp = await indexRecord(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        reinstate: async (input: ReinstateProps) => {
          const resp = await reinstate(input)
          if (resp.success) {
            const newDeactivationDate = estimateReinstatedDeactivationDate()
            usersService.send('UPDATE', {
              data: {
                user: {
                  id: input.userId,
                  status: 'review',
                  deactivationDate: newDeactivationDate.format('YYYY-MM-DD'),
                },
              },
            })
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        reactivate: async (input: ReactivateProps) => {
          const resp = await reactivate(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        updateProgress: async (input: UpdateProgressProps) => {
          const resp = await progress(input)
          if (resp.success) {
            usersService.send('PROGRESS', {
              data: {
                user: {
                  id: input.userId,
                  lessonNumber: input.lessonNumber,
                  partNumber: input.partNumber,
                },
              },
            })
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        resendLMSSignatureRequest: async (
          input: ResendLMSSignatureRequestProps
        ) => {
          const resp = await resendLMSSignatureRequest(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        reactivateTMATT: async (input: ReactivateTMATTProps) => {
          const resp = await reactivateTMATT(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        deactivateTMATT: async (input: DeactivateTMATTProps) => {
          const resp = await deactivateTMATT(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
        enrollTMATT: async (input: EnrollTMATTProps) => {
          const resp = await enrollTMATT(input)
          if (resp.success) {
            return resp
          } else {
            throw Error(resp.error)
          }
        },
      },
    },
    locationActions: {
      read: async (input: ReadLocationProps) => {
        try {
          locationsService.send('LOAD', {
            data: [{ id: input.id, __meta: { status: 'pending' } }],
          })
          const location = await readLocation(input)
          locationsService.send('LOAD', {
            data: [{ ...location, __meta: { status: 'success' } }],
          })
          return location
        } catch (error: any) {
          locationsService.send('LOAD', {
            data: [
              {
                id: input.id,
                __meta: {
                  status: 'failed',
                  error,
                  displayError: 'Error retrieving data for this location',
                },
              },
            ],
          })
          throw error
        }
      },
      create: async (input: CreateLocationProps) => {
        try {
          const location = await createLocation(input)
          locationsService.send('LOAD', {
            data: [{ ...location, __meta: { status: 'success' } }],
          })
          return location
        } catch (error: any) {
          throw error
        }
      },
      update: async (input: UpdateLocationProps) => {
        try {
          locationsService.send('LOAD', {
            data: [{ id: input.id, __meta: { status: 'pending' } }],
          })
          const location = await updateLocation(input)
          locationsService.send('LOAD', {
            data: [{ ...location, __meta: { status: 'success' } }],
          })
          return location
        } catch (error: any) {
          locationsService.send('LOAD', {
            data: [
              {
                id: input.id,
                __meta: {
                  status: 'failed',
                  error,
                  displayError: 'Error updating data for this location',
                },
              },
            ],
          })
          throw error
        }
      },
    },
    orgActions: {
      list: async (input: { countryCode: string }) => {
        try {
          requested.current.orgs.list.push({ input })
          orgService.send('LOAD', {
            data: [{ id: input.countryCode, __meta: { status: 'pending' } }],
          })
          const response = await listOrgs(input)
          orgService.send('LOAD', {
            data: [
              {
                id: input.countryCode,
                orgs: response.results,
                __meta: { status: 'success' },
              },
            ],
          })
          return response.results
        } catch (error: any) {
          orgService.send('LOAD', {
            data: [
              {
                id: input.countryCode,
                __meta: {
                  status: 'failed',
                  error,
                  displayError:
                    'There was an error getting the orgs for this country.',
                },
              },
            ],
          })
          throw error
        }
      },
    },
  }
}
